import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../../../assets/environments/environment';
import {commonProperties} from '../../../../assets/environments/environment.common';
import {AuthenticationService} from '../../../core/services/authentication.service';
import {CompanyItemModel} from '../../../shared/models/companies/company-item.model';
import {BankModel, LegalInformationModel} from '../../../shared/models/companies/legal-information.model';
import {ListContentModel} from '../../../shared/models/list-content.model';
import {CompanyTaxesPostModel, TaxNumber} from '../../../shared/models/companies/CompanyTaxesModel';
import { CompanyCreationBodyModel } from '../../../shared/models/companies/company-creation-body.model';
import { PatchParamsModel } from "@i-supplier/angular-shared-module";


@Injectable({
  providedIn: 'root'
})
export class MyCompanyService {
  billableCompanies = 'invoicingMgtApi/invoice-configs/billable-companies';

  private companyToUpdateSubject = new BehaviorSubject(null);

  constructor(private httpClient: HttpClient, private authService: AuthenticationService) {
  }

  getMyCompany(): Observable<LegalInformationModel> {
    return this.httpClient.get<LegalInformationModel>(environment.api_root + commonProperties.myCompany.replace(':companyId', this.authService.decodeToken('tenantId')));
  }

  isFrenchCompany(origin: any): boolean {
    if (!origin) {
      return false;
    }
    if (typeof origin === 'string') {
      const normalized = origin.toUpperCase();
      return normalized === 'FR' || normalized === 'FRANCE';
    }
    const code = (origin.alpha2Code || origin.code || origin.name || '').toString().toUpperCase();
    return code === 'FR' || code === 'FRANCE';
  }

  getSuppliersCompaniesByPage(search: string, page: number, size: number): Observable<ListContentModel> {
    let params = new HttpParams();
    params = params.set('name', search).set('page', page.toString()).set('size', size.toString()).set('types', 'SUPPLIER');
    return this.httpClient.get<ListContentModel>(environment.api_root + commonProperties.suppliersList, {params});
  }

  getSuppliersById(supplierId): Observable<CompanyItemModel> {
    return this.httpClient.get<CompanyItemModel>(environment.api_root + commonProperties.supplierById.replace(':supplierId', supplierId));
  }

  getLegalForms(): Observable<any> {
    return this.httpClient.get(environment.api_root + commonProperties.legalForms);
  }

  mapLegalInformationFormToModel(value: any): LegalInformationModel {
    return Object.assign(new LegalInformationModel(), {
      id: value.id,
      name: value.name,
      managementMailbox: value.managementMailbox,
      origin: value.origin.alpha2Code,
      incorporationNumber: value.incorporationNumber,
      registrationCity: value.registrationCity,
      shareCapitalAmount: value.shareCapitalAmount,
      shareCapitalCurrency: value.shareCapitalCurrency,
      registrationNumber: value.registrationNumber,
      subjectToVat: value.TAX?.subjectToVat,
      legalForm: value.legalForm,
      codeTiers: value.codeTiers,
      phoneNumber: value?.phoneNumber?.internationalNumber?.replace(/\s/g, '')?.replace('-', ''),
      ...(value.legalForm === 'OTHER' && {customLegalForm: value.customLegalForm}),
      ...(value.bank && {
        bank: Object.assign(new BankModel(), {
          bankName: value.bank.bankName,
          iban: value.bank.iban,
          bicOrSwift: value.bank.bicOrSwift,
          ibanType: value.bank.ibanType
        })
      })
    });
  }

  updateCompanyById(params: PatchParamsModel<LegalInformationModel>, companyId: string) {
    return this.httpClient.patch(`${environment.api_root}enterpriseMgtApi/companies/${companyId}`, params);
  }

  getCompanyToUpdate() {
    return this.companyToUpdateSubject.asObservable();
  }

  setCompanyToUpdate(company) {
    this.companyToUpdateSubject.next(company);
  }

  getUserAllowedRolesForEmployeeCreation(): Observable<any> {
    return this.httpClient.get(environment.api_root + commonProperties.authorities);
  }

  getCountries() {
    return this.httpClient.get(environment.api_root + commonProperties.countries);
  }

  getCompanyById(companyId: string) {
    return this.httpClient.get(environment.api_root + commonProperties.myCompany.replace(':companyId', companyId));
  }

  getCompanyRoles(types: string) {
    let params = new HttpParams();
    params = params.set('types', types);
    return this.httpClient.get(environment.api_root + commonProperties.authoritiesByCompanyType, {params});
  }

  getBillableCompaniesByPage(page: number, size: number, sort: string, search: string): Observable<ListContentModel> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    params = params.set('page', page.toString()).set('size', size.toString()).set('sort', sort);
    return this.httpClient.get<ListContentModel>(environment.api_root + this.billableCompanies, {params});
  }

  mapLegalInformationToCompanyTaxesPostModel(tvaFormValue: { taxGroupId: string; numbers: TaxNumber[]; }) {
    const companyTaxesPostModel: CompanyTaxesPostModel = new CompanyTaxesPostModel();
    companyTaxesPostModel.numbers = [];
    companyTaxesPostModel.taxGroupId = tvaFormValue.taxGroupId;
    tvaFormValue.numbers.map(taxType => companyTaxesPostModel.numbers.push({number: taxType.number, taxTypeId: taxType.taxTypeId}));
    return companyTaxesPostModel;
  }

  getIdentityAvailable(country: string, registrationNumber: string, incorporationNumber: string, newCompany: boolean = false): Observable<{ valid: boolean }> {
    let params = new HttpParams().set('country', country);
    if (!newCompany) {
      params = params.set('companyId', this.authService.decodeToken('tenantId'));
    }
    if (registrationNumber) {
      params = params.set('registrationNumber', registrationNumber);
    }
    if (incorporationNumber) {
      params = params.set('incorporationNumber', incorporationNumber);
    }
    return this.httpClient.get<{ valid: boolean }>(`${environment.api_root}enterpriseMgtApi/companies/identity`, {params});
  }
}
